import {
  Bell,
  CreditCard,
  FileText,
  Headphones,
  Home,
  KeyRound,
  RefreshCw,
  UserRoundCheck,
} from 'lucide-react';

const ALWAYS_VISIBLE_PERMISSIONS = new Set(['dashboard', 'changePassword']);

const normalizePermissionStatus = (permissions, permissionId) => {
  if (ALWAYS_VISIBLE_PERMISSIONS.has(permissionId)) return 'visible';
  const value = permissions?.[permissionId];
  if (value === true || value === 'visible') return 'visible';
  return 'hidden';
};

/**
 * Fonte única de navegação visual do Portal do Servidor.
 *
 * Importante: esta função controla somente visibilidade/experiência do frontend.
 * A autorização real continua nos contratos atuais de Auth/Rules/backend.
 */
export const buildServerNavigation = ({
  permissions = {},
  vinculoSelecionado = null,
  recadastramentoElegivel = false,
  recadastramentoStatus = null,
} = {}) => {
  const contrachequePath = vinculoSelecionado?.matricula
    ? `/contracheques/${vinculoSelecionado.matricula}`
    : '/dashboard';

  const items = [
    {
      id: 'dashboard',
      label: 'Início',
      shortLabel: 'Início',
      icon: Home,
      path: '/dashboard',
      category: 'Principal',
      permissionId: 'dashboard',
      mobilePrimary: true,
    },
    {
      id: 'contracheque',
      label: 'Contracheques',
      shortLabel: 'Contracheque',
      icon: CreditCard,
      path: contrachequePath,
      category: 'Serviços',
      permissionId: 'contracheque',
      mobilePrimary: true,
    },
    {
      id: 'requerimentos',
      label: 'Solicitações',
      shortLabel: 'Solicitações',
      icon: FileText,
      path: '/requerimentos',
      category: 'Serviços',
      permissionId: 'requerimentos',
      mobilePrimary: true,
    },
    {
      id: 'suporte',
      label: 'Atendimento ao DP',
      shortLabel: 'Atendimento',
      icon: Headphones,
      path: '/ajuda',
      category: 'Atendimento',
      permissionId: 'suporte',
      mobilePrimary: true,
    },
    {
      id: 'ficha',
      label: 'Ficha Funcional',
      shortLabel: 'Ficha',
      icon: UserRoundCheck,
      path: '/ficha-funcional',
      category: 'Serviços',
      permissionId: 'fichaFuncional',
      mobilePrimary: false,
    },
    {
      id: 'recadastramento',
      label: 'Recadastramento',
      shortLabel: 'Recadastramento',
      icon: RefreshCw,
      path: '/recadastramento',
      category: 'Pendências e campanhas',
      permissionId: 'recadastramento',
      mobilePrimary: false,
      requiresRecadastramentoEligibility: true,
      state: ['pendente_revisao', 'aceito', 'solicitacao_edicao'].includes(recadastramentoStatus)
        ? { goToReview: true }
        : undefined,
    },
    {
      id: 'avisos',
      label: 'Avisos',
      shortLabel: 'Avisos',
      icon: Bell,
      path: '/avisos',
      category: 'Comunicação',
      permissionId: 'avisos',
      mobilePrimary: false,
    },
    {
      id: 'senha',
      label: 'Alterar senha',
      shortLabel: 'Senha',
      icon: KeyRound,
      path: '/change-password',
      category: 'Conta',
      permissionId: 'changePassword',
      mobilePrimary: false,
    },
  ];

  return items
    .map((item) => ({
      ...item,
      permissionStatus: normalizePermissionStatus(permissions, item.permissionId),
    }))
    .filter((item) => {
      if (item.permissionStatus === 'hidden') return false;
      if (item.requiresRecadastramentoEligibility && !recadastramentoElegivel) return false;
      return true;
    });
};

export const groupServerNavigation = (items = []) => {
  const order = ['Principal', 'Serviços', 'Pendências e campanhas', 'Atendimento', 'Comunicação', 'Conta'];
  return order
    .map((category) => ({
      category,
      items: items.filter((item) => item.category === category),
    }))
    .filter((group) => group.items.length > 0);
};
